import { useContext } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Cookies from 'js-cookie'

import { getAuthToken } from '@library/auth'
import { canUseWindow } from '@library/environment'

/**
 * Feature flag hook to check if Basket Comprehension experiment is enabled
 * Uses Optimizely experiment: beetroots_ofx_basket_comprehension
 *
 * @returns {boolean} true if Basket Comprehension experiment should be shown
 */
export const useIsBasketComprehensionEnabled = (): boolean => {
  const { optimizely } = useContext(OptimizelyContext)

  // Return false early if not in browser environment to avoid server-side execution
  if (!canUseWindow()) {
    return false
  }

  const authToken = getAuthToken()
  const isAuthenticated = !!authToken

  let hasAcceptedAnalytics = false
  try {
    const consentCookieString = Cookies.get('v1_gousto_cookie_consent')
    const consentCookie = consentCookieString ? JSON.parse(consentCookieString) : {}
    hasAcceptedAnalytics = consentCookie?.categories?.includes('ANALYTICS')
  } catch (error) {
    let errorMessage = 'Unknown error when parsing cookie consent'
    if (error instanceof Error) {
      errorMessage = error.message
      datadogLogs.logger.error('Error parsing BasketComprehension cookie consent', {
        error: errorMessage,
        cookieConsent: Cookies.get('v1_gousto_cookie_consent'),
      })
    }
  }

  // We only allocate users to the experiment if they have consented
  // to the Analytics cookies and are not authenticated (unless bypassed)
  let enabled = false
  let variationKey: string | null | undefined

  if (optimizely && hasAcceptedAnalytics && !isAuthenticated) {
    try {
      const decision = optimizely.decide('beetroots_ofx_basket_comprehension')
      enabled = decision.enabled
      variationKey = decision.variationKey
    } catch (error) {
      datadogLogs.logger.error('Error deciding basket comprehension', {
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const isEnabled = enabled && variationKey === 't1'

  return isEnabled
}
